import { useEffect, useRef, useState } from "react";
import { Box, InputAdornment, TextField, Typography } from "@mui/material";
import { EditOutlined } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

export const MAX_RESOURCE_CATERING_ORDER_QTY = 99;

export function parseResourceCateringQuantity(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === "") return null;
  const parsed = Number.parseInt(trimmed, 10);
  return Number.isFinite(parsed) ? parsed : null;
}

export function isResourceCateringQuantityValid(
  quantity: number | null,
  maxQty = MAX_RESOURCE_CATERING_ORDER_QTY
): boolean {
  return quantity != null && quantity >= 1 && quantity <= maxQty;
}

type ResourceCateringQuantityFieldProps = Readonly<{
  initialQty: number;
  maxQty?: number;
  onQuantityChange: (quantity: number) => void;
  onValidityChange?: (invalid: boolean) => void;
}>;

const qtyFieldSx = {
  width: 96,
  "& input[type=number]": {
    textAlign: "center",
    MozAppearance: "textfield",
    padding: "4px 4px 4px 12px"
  },
  "& input[type=number]::-webkit-outer-spin-button": { WebkitAppearance: "none", margin: 0 },
  "& input[type=number]::-webkit-inner-spin-button": { WebkitAppearance: "none", margin: 0 }
} as const;

export function ResourceCateringQuantityField({
  initialQty,
  maxQty = MAX_RESOURCE_CATERING_ORDER_QTY,
  onQuantityChange,
  onValidityChange
}: ResourceCateringQuantityFieldProps) {
  const { t } = useTranslation();
  const [value, setValue] = useState(String(initialQty));
  const lastInvalidRef = useRef(!isResourceCateringQuantityValid(initialQty, maxQty));

  const reportValidity = (quantity: number | null) => {
    const invalid = !isResourceCateringQuantityValid(quantity, maxQty);
    if (invalid !== lastInvalidRef.current) {
      lastInvalidRef.current = invalid;
      onValidityChange?.(invalid);
    }
  };

  useEffect(() => {
    setValue(String(initialQty));
    reportValidity(initialQty);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync when resource changes
  }, [initialQty, maxQty]);

  useEffect(() => {
    reportValidity(parseResourceCateringQuantity(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initial validity only
  }, []);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.target.value;
    setValue(nextValue);
    const parsed = parseResourceCateringQuantity(nextValue);
    reportValidity(parsed);
    if (isResourceCateringQuantityValid(parsed, maxQty) && parsed != null) {
      onQuantityChange(parsed);
    }
  };

  const parsed = parseResourceCateringQuantity(value);
  const showError = value.trim() !== "" && !isResourceCateringQuantityValid(parsed, maxQty);
  const helperText = (() => {
    if (!showError) return undefined;
    if (parsed != null && parsed > maxQty) return t("Order quantity exceeds available amount");
    return t("Please select the available quantity");
  })();

  return (
    <Box
      data-testid="resource-catering-quantity-field"
      sx={{ borderTop: 1, borderColor: "divider", pt: 1.5 }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
        <Typography variant="body1" color="text.secondary">
          {t("Qty")}
        </Typography>
        <TextField
          type="number"
          size="small"
          value={value}
          onChange={handleChange}
          error={showError}
          helperText={helperText}
          sx={qtyFieldSx}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <EditOutlined fontSize="inherit" color="action" />
                </InputAdornment>
              )
            },
            htmlInput: {
              min: 1,
              max: maxQty,
              "data-testid": "resource-catering-quantity-input"
            }
          }}
        />
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
        {t("_resourceCateringQuantity_hint")}
      </Typography>
    </Box>
  );
}
